import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { backupState, type BackupRun } from './backups';

function size(bytes: number | null): string {
  if (bytes === null) return '—';
  const mb = bytes / 1024 / 1024;
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
}

function duration(run: BackupRun): string {
  if (!run.finished_at) return '—';
  const seconds = (Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000;
  if (seconds < 90) return `${Math.round(seconds)} s`;
  return `${Math.round(seconds / 60)} min`;
}

const COLUMNS = 6;

/**
 * One run, with its log underneath rather than beside it.
 *
 * The message is the tail of a shell log — several lines, some of them a
 * 64-character snapshot id. As a table column it has no width that works: wide
 * enough to read is wide enough to push the other six off the screen. So it
 * lives in a row of its own, spanning the table, where it can simply wrap.
 *
 * Failures start expanded. "Something went wrong, now go and click" is one step
 * too many for the screen whose entire job is to say what went wrong.
 */
function Row({ run }: { run: BackupRun }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(run.status === 'failed');

  const when = (value: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language) : '—';

  return (
    <Fragment>
      <tr>
        <td>
          {run.message ? (
            <button type="button" className="link" onClick={() => setOpen((value) => !value)}>
              {when(run.started_at)}
            </button>
          ) : (
            when(run.started_at)
          )}
        </td>
        <td>{t(`backups.kinds.${run.kind}`)}</td>
        <td>
          <span className={`badge badge--backup-${run.status}`}>
            {t(`backups.statuses.${run.status}`)}
          </span>
          {run.checked_at && <span className="badge badge--checked">{t('backups.verified')}</span>}
        </td>
        <td>{duration(run)}</td>
        <td>{size(run.dump_bytes)}</td>
        {/* Eight characters is what restic itself accepts and prints, and what
            a person retypes into a restore command without losing their place. */}
        <td className="backups__snapshots">
          {run.database_snapshot ? (
            <>
              <code>{run.database_snapshot.slice(0, 8)}</code>
              <code>{run.media_snapshot?.slice(0, 8) ?? '—'}</code>
            </>
          ) : (
            '—'
          )}
        </td>
      </tr>

      {open && run.message && (
        <tr className="admin__detail-row">
          <td colSpan={COLUMNS}>
            <pre className="backups__log">{run.message}</pre>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

/**
 * What the backup job has been doing.
 *
 * The banner above the page is the part that matters — this list is for the
 * question that follows it, which is always "what happened". Snapshot ids are
 * shown rather than hidden because they are what a restore is given, and
 * hunting for them in a container log at the moment of a restore is the wrong
 * time to be hunting for anything.
 */
export function BackupsPage() {
  const { t, i18n } = useTranslation();
  const state = useQuery({ queryKey: ['backups'], queryFn: () => backupState(50) });

  const when = (value: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language) : '—';

  const health = state.data?.health;

  return (
    <section className="panel">
      <h2>{t('backups.title')}</h2>

      {health && (
        <div className={health.stale ? 'backups__health is-stale' : 'backups__health'}>
          {health.last_success_at ? (
            <p>
              {t('backups.lastSuccess', { when: when(health.last_success_at) })}
              {health.age_hours !== null && ` (${t('backups.ago', {
                hours: Math.round(health.age_hours),
              })})`}
            </p>
          ) : (
            <p>{t('backups.never')}</p>
          )}
          {health.stale && <p className="backups__alarm">{t('backups.staleWarning', {
            hours: health.stale_after_hours,
          })}</p>}
          {health.last_status === 'failed' && (
            <p className="backups__alarm">{t('backups.lastFailed')}</p>
          )}
        </div>
      )}

      {/* Where the repository lives and what unlocks it are host facts the
          server cannot read, and printing a half-truth here would be worse than
          saying where to look. */}
      <p className="admin__hint">{t('backups.whereHint')}</p>

      {state.data?.runs.length ? (
        <div className="scroll-x">
          <table className="admin__table backups__table">
            <thead>
              <tr>
                <th>{t('backups.started')}</th>
                <th>{t('backups.kind')}</th>
                <th>{t('backups.status')}</th>
                <th>{t('backups.duration')}</th>
                <th>{t('backups.dump')}</th>
                <th>{t('backups.snapshots')}</th>
              </tr>
            </thead>
            <tbody>
              {state.data.runs.map((run) => (
                <Row key={run.id} run={run} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="admin__hint">{t('backups.none')}</p>
      )}
    </section>
  );
}
